import {Component, Input, OnInit} from '@angular/core';
import {SupportContact} from '@models/adminstration/organization/support-contact/support-contact';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';

@Component({
    selector: 'scitis-admin-organization-contact-modal',
    templateUrl: './contact-modal.component.html',
    styleUrls: ['./contact-modal.component.scss'],
    standalone: false
})
export class ContactModalComponent implements OnInit {
  private imageMaxSize: number = 500 * 1024;
  private defaultImage: string = './assets/img/fallback_avatar.png';
  isNewSupportContact: boolean = false;
  submitted: boolean = false;
  contactForm: UntypedFormGroup = new UntypedFormGroup({});
  @Input()
  contact: SupportContact = new SupportContact();

  constructor(private activeModal: NgbActiveModal,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private translation: TranslateService) {

  }

  ngOnInit(): void {
    this.contactForm = new UntypedFormGroup({
      name: new UntypedFormControl(this.contact.name, [Validators.required]),
      phone: new UntypedFormControl(this.contact.phone, [Validators.required]),
      email: new UntypedFormControl(this.contact.email, [Validators.required, Validators.email]),
      type: new UntypedFormControl(this.contact.type, [Validators.required]),
    });

    this.initContact();

    this.isNewSupportContact = this.contact.id === -1;

  }

  private initContact() {
    if (!this.contact) {
      this.contact = new SupportContact();
    }

    if (!this.contact.image || this.contact.image.length === 0) {
      this.contact.image = this.defaultImage;
    }
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.contactForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.submitted ? this.contactForm.invalid : this.submitted && this.contactForm.invalid;
  };

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.contactForm.invalid) {
      return;
    }

    this.contact.type = this.f.type.value;
    this.contact.name = this.f.name.value;
    this.contact.phone = this.f.phone.value;
    this.contact.email = this.f.email.value;


    this.activeModal.close(this.contact);
  }

  onImageChange($event: Event) {
    if (!$event.target) {
      return;
    }
    const target = $event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return;
    }
    this.loadingIndicator.show();

    const file = target.files[0];

    if (!file) {
      this.loadingIndicator.hide();
      return;
    }

    if (!this.isImageFile(file)) {
      const message = this.translation.instant('file is not an image');
      this.notification.error(file.name + ' ' + message, null, 'ContactModal', true);
      this.loadingIndicator.hide();
      return;
    }

    if (file.size >= this.imageMaxSize) {
      let message = this.translation.instant('Uploaded file size "#1" is to big (max 500Kb)');
      message = message.replace('#1', file.size);
      this.loadingIndicator.hide();
      this.notification.warning(message, 'ContactModal');
      return;
    }


    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      this.contact.image = String(reader.result);
      this.loadingIndicator.hide();
    };
  }

  private isImageFile(file: File): boolean {
    const fileType = file.type;

    return fileType.includes('image');
  }

}
